import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import '../../styles/classroom/ClassroomHome.css'
import { ReactComponent as Collapsedlogo } from '../../assets/svg/US-collapsed.svg'
import { ReactComponent as CollapsedEarlylogo } from '../../assets/svg/US-Early-collapsed.svg'
import { useUser } from '../../auth'
import { get_my_class } from '../../server/server'
import 'react-datepicker/dist/react-datepicker.css'
import { emotions_color, emotions_list, primary_emotions_list } from '../../constants/Constants'

import FeedBackModal from '../modals/FeedBackModal/FeedBackModal'

import { useTranslation } from 'react-i18next'

import { formatDate, notify, usePageTitle } from '../../utils/util'
import { csvCodec, dateCodec, oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import { useElementWidth } from '../../hooks/useElementWidth'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

import Loading from '../Loading'
import { isSameDay, isValid } from 'date-fns'

import { useInstitution } from '../../auth/InstitutionContext'
import { useAppContext } from '../../context/AppContext'
import ClassroomTrendSkeleton from './skeleton/ClassroomTrendSkeleton'
import SkeletonClassroomChartHeader from './skeleton/SkeletonClassroomChartHeader'
import ClassroomSkeletonList from './skeleton/ClassroomSkeletonList'
import ClassroomTeachersList from './ClassroomTeachersList'
import AnalyticsHeaderSkeleton from '../analytics/skeleton/AnalyticsHeaderSkeleton'
import { CommentTooltip } from './StudentCard'

const ClassroomTrend = lazyWithRetry(() => import('./ClassroomTrend'), './ClassroomTrend')

const ClassroomChartHeader = lazyWithRetry(() => import('./ClassroomChartHeader'), './ClassroomChartHeader')

const ClassroomStudentsList = lazyWithRetry(() => import('./ClassroomStudentList'), './ClassroomStudentList')

const PrimaryClassesHeader = lazyWithRetry(() => import('./PrimaryClassesHeader'), './PrimaryClassesHeader')

// Constants
const MILLISECONDS_PER_DAY = 60 * 60 * 24 * 1000
const DEFAULT_COLUMN_WIDTH = 220
const DEFAULT_EMOTIONS_PER_ROW = 6
const OPACITY_VALUE = '66'
const DISPLAY_MODES = ['primary', 'regular']
const ALL_FILTER = ['all']
const SEARCH_URL_DEBOUNCE_MS = 300
// Index at which the "uncomfortable" emotions start in each mode's list:
// regular -> numb..angry (7), primary -> sad, afraid, angry (3).
const UNCOMFORTABLE_START = { regular: 5, primary: 3 }
// The students tab is the default and stays out of the URL.
const tabCodec = {
    parse: (raw) => (raw === 'staff' ? false : raw === 'students' ? true : undefined),
    serialize: (showStudents) => (showStudents ? 'students' : 'staff'),
}

const uncomfortableOf = (list, isRegular) => list.slice(isRegular ? UNCOMFORTABLE_START.regular : UNCOMFORTABLE_START.primary)

/** One entry per user who answered, so a user in several classes counts once. */
const dedupeAnswers = (entries) => {
    const byUser = new Map()
    entries.forEach((entry) => {
        const emotion = entry?.feedback?.emotion
        if (!emotion) return
        const userId = entry?.feedback?.user_id
        if (!byUser.has(userId)) byUser.set(userId, { user_id: userId, emotion })
    })
    return Array.from(byUser.values())
}

const buildChartData = (emotions, answers) => {
    const counts = {}
    answers.forEach((emotion) => {
        if (emotion) counts[emotion] = (counts[emotion] || 0) + 1
    })
    return emotions.map((label) => ({ label, value: counts[label] || 0, color: emotions_color[label], selected: false }))
}

/** Full color for selected emotions, translucent for the rest. */
const recolorChart = (data, selectedEmotions) =>
    data.map((entry) => {
        const label = entry.label.toLowerCase()
        const selected = selectedEmotions.includes(label)
        return { ...entry, color: emotions_color[label] + (selected ? '' : OPACITY_VALUE), selected }
    })

/** Keeps only the students matching `predicate`, dropping classes left empty. */
const filterClasses = (classes, predicate) =>
    (classes || []).map((_class) => ({ ..._class, data: (_class?.data || []).filter(predicate) })).filter((_class) => _class.data.length > 0)

const matchesName = (item, text) => {
    const fullName = `${item?.user?.first_name || ''} ${item?.user?.last_name || ''}`.toLowerCase()
    return fullName.includes(text.toLowerCase())
}

function ClassroomHome() {
    usePageTitle('Upstrive Education - Classroom')
    const scrollRef = useRef()
    const { t } = useTranslation()
    const { appState } = useAppContext()
    const history = useHistory()
    // ?emotion= (singular) is an inbound deep-link alias used by links from
    // other screens. Both params are read exactly once, at mount; the alias is
    // normalized away below, and later URL writes must not re-trigger this.
    const [{ emotionParam, emotionsParam }] = useState(() => {
        const params = new URLSearchParams(history.location.search)
        return { emotionParam: params.get('emotion'), emotionsParam: params.get('emotions') }
    })
    const { user } = useUser()
    const { institution } = useInstitution()
    const [loading, setLoading] = useState(true)
    const [initLoading, setInitLoading] = useState(true)
    const [myStudents, setMyStudents] = useState([])
    const [usageType, setUsageType] = useState('student')
    const [myVisibleStudents, setMyVisibleStudents] = useState([])
    const [resultDataChart, setResultDataChart] = useState([])
    const maxDate = useMemo(() => new Date(), [])
    const primaryUI = !!appState?.primary
    const userDefaultDisplayMode = user.hasOnlyPrimaryStudents || primaryUI ? 'primary' : 'regular'
    // userDefaultDisplayMode depends on context that can resolve after mount, so
    // the hook re-reads it on every write rather than capturing it.
    const [displayMode, setDisplayMode] = useUrlState('mode', userDefaultDisplayMode, oneOfCodec(DISPLAY_MODES))
    const [startDate, setStartDate] = useUrlState('date', () => new Date(), {
        ...dateCodec,
        isDefault: (d) => isSameDay(d, new Date()),
    })
    const initialIsRegular = displayMode === 'regular'
    const initialListOfEmotions = initialIsRegular ? emotions_list : primary_emotions_list
    const getInitialSelectedLabels = () => {
        if (emotionsParam) {
            const labels = emotionsParam.split(',').filter(Boolean)
            if (labels.length > 0) return labels
        }
        if (emotionParam) {
            return emotionParam === 'comments' ? uncomfortableOf(initialListOfEmotions, initialIsRegular) : [emotionParam]
        }
        return initialListOfEmotions
    }
    const initialSelectedLabels = getInitialSelectedLabels()
    const getInitialSelectedEmotion = () => {
        if (emotionParam) return emotionParam
        if (emotionsParam) {
            if (initialSelectedLabels.length === 1) return initialSelectedLabels[0]
            if (initialSelectedLabels.length === initialListOfEmotions.length) return 'All emotions'
            const uncomfortable = uncomfortableOf(initialListOfEmotions, initialIsRegular)
            if (initialSelectedLabels.length === uncomfortable.length && initialSelectedLabels.every((l) => uncomfortable.includes(l))) {
                return 'Uncomfortable'
            }
        }
        return 'Emotions'
    }
    const [selectedEmotion, setSelectedEmotion] = useState(getInitialSelectedEmotion())
    // Default is dynamic: `mode` decides whether the full list is the 12 regular
    // or 6 primary emotions, so "is this the default" cannot be a fixed value.
    const [selectedLabels, setSelectedLabels] = useUrlState('emotions', getInitialSelectedLabels, {
        ...csvCodec(),
        isDefault: (labels) => labels.length === listOfEmotions.length,
    })

    const [showCommentLabel, setShowContentLabel] = useState(emotionParam === 'comments')
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [selectedStudent, setSelectedStudent] = useState({})
    const [openStudentFeedback, setOpenStudentFeedback] = useState(false)

    const [showDropdown, setShowDropdown] = useState(false)
    const [searchText, setSearchText] = useUrlState('search', '', { debounce: SEARCH_URL_DEBOUNCE_MS })
    const searchRef = useRef()

    const [filterYears, setFilterYears] = useState([])
    const [classesOfYear, setClassesOfYear] = useState({})

    const [selectedYearList, setSelectedYearList] = useState(null)
    // Only the *applied* filters are URL-backed; the selected* pair is the
    // dropdown's draft and must not leak into the URL before Apply is pressed.
    const [appliedYearFilter, setAppliedYearFilter] = useUrlState('years', ALL_FILTER, {
        ...csvCodec(),
        isDefault: (v) => v.includes('all'),
    })
    const [appliedClassFilter, setAppliedClassFilter] = useUrlState('classes', ALL_FILTER, {
        ...csvCodec(),
        isDefault: (v) => v.includes('all'),
    })
    const [selectedYearFilter, setSelectedYearFilter] = useState(appliedYearFilter)
    const [selectedClassFilter, setSelectedClassFilter] = useState(appliedClassFilter)
    const [loadingFilter, setLoadingFilter] = useState(false)
    const [loadingStaffFilter, setLoadingStaffFilter] = useState(false)
    const [numberOfStudentsWhoAnswered, setNumberOfStudentsWhoAnswered] = useState(0)
    const [numberOfTeachersWhoAnswered, setNumberOfTeachersWhoAnswered] = useState(0)

    const [showStudents, setShowStudents] = useUrlState('tab', true, tabCodec)
    const [teachersList, setTeachersList] = useState([])
    const [myVisibleTeachersList, setMyVisibleTeachersList] = useState([])
    const hideStaffButton = useMemo(() => institution.restrictedStaffMoodAccess && !institution.privilegedUsersForStaffMood.includes(user.id), [institution, user])
    const isRegularDisplayMode = displayMode === 'regular'

    const listOfEmotions = isRegularDisplayMode ? emotions_list : primary_emotions_list

    // Per-class presentation state (collapsed, trimmed to one row), keyed by a
    // stable class key. Kept off the data objects so filters and searches,
    // which rebuild those objects, never disturb it.
    const [classUiState, setClassUiState] = useState({})
    const toggleClassUi = useCallback((classKey, field) => {
        setClassUiState((prev) => ({ ...prev, [classKey]: { ...prev[classKey], [field]: !prev[classKey]?.[field] } }))
    }, [])
    // Bumped on every successful load; drives the card entrance animation so it
    // replays for new data but not for local filtering.
    const [dataVersion, setDataVersion] = useState(0)

    // How many 200px cards fit one row of the list; measured from the title,
    // which spans the same width, and kept fresh across resizes.
    const [titleRef, titleWidth] = useElementWidth()
    const reduce = titleWidth > 0 ? Math.max(1, Math.floor(titleWidth / DEFAULT_COLUMN_WIDTH)) : DEFAULT_EMOTIONS_PER_ROW

    const buildFilterQuery = useCallback(() => {
        const query = {}
        if (!appliedYearFilter.includes('all')) query.years = appliedYearFilter
        if (!appliedClassFilter.includes('all')) query.classes = appliedClassFilter
        return query
    }, [appliedYearFilter, appliedClassFilter])
    const buildLoadQuery = useCallback(() => (showStudents ? buildFilterQuery() : { class: 'staff' }), [showStudents, buildFilterQuery])
    const currentTargetType = useCallback(() => (showStudents ? 'student' : 'teacher'), [showStudents])

    // Fold the inbound ?emotion= alias into the canonical ?emotions= exactly
    // once. Both must never coexist: getInitialSelectedEmotion() prefers
    // `emotion` while getInitialSelectedLabels() prefers `emotions`, so a stale
    // singular would relabel the dropdown to something the filter isn't doing.
    useEffect(() => {
        if (!emotionParam) return
        const params = new URLSearchParams(history.location.search)
        params.delete('emotion')
        if (initialSelectedLabels.length !== initialListOfEmotions.length) {
            params.set('emotions', initialSelectedLabels.join(','))
        }
        const search = params.toString().split('%2C').join(',')
        history.replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const filterTeacherData = useCallback(
        (selectedEmotions, data = resultDataChart, teachers = teachersList) => {
            setResultDataChart(recolorChart(data, selectedEmotions))
            if (selectedEmotions.length === listOfEmotions.length) {
                setMyVisibleTeachersList(teachers || [])
            } else {
                setMyVisibleTeachersList((teachers || []).filter((teacher) => selectedEmotions.includes(teacher?.feedback?.emotion)))
            }
        },
        [resultDataChart, teachersList, listOfEmotions]
    )

    const filterData = useCallback(
        (selectedEmotions, data = resultDataChart, students = myStudents, selectedLabel = selectedEmotion) => {
            setResultDataChart(recolorChart(data, selectedEmotions))
            if (selectedEmotions.length === listOfEmotions.length) {
                setMyVisibleStudents((students || []).filter((_class) => _class?.data?.length))
            } else if (selectedLabel === 'comments') {
                setMyVisibleStudents(
                    filterClasses(students, (student) => student.feedback?.message !== '' && !student?.feedback?.empty_data && selectedEmotions.includes(student.feedback?.emotion))
                )
            } else {
                setMyVisibleStudents(filterClasses(students, (student) => selectedEmotions.includes(student.feedback?.emotion)))
            }
        },
        [resultDataChart, myStudents, selectedEmotion, listOfEmotions]
    )

    // Only the most recent request may update the screen. Rapid day stepping or
    // tab switching fires overlapping requests, and a slow older response must
    // not overwrite a newer one. Unmount invalidates whatever is in flight.
    const requestIdRef = useRef(0)
    useEffect(
        () => () => {
            requestIdRef.current += 1
        },
        []
    )

    const loadMyClass = async (display_day, query = {}, userTargetType = 'student', isPrimary = !isRegularDisplayMode) => {
        const requestId = ++requestIdRef.current
        const payload = {
            user: user,
            number_of_students: 0,
            day: Date.parse(display_day),
            day_code: formatDate(new Date(display_day)),
            query,
        }
        if (isPrimary) payload.primaryClassesOnly = true
        const resp = await get_my_class(payload)
        if (requestId !== requestIdRef.current) return

        if (resp?.status === 200) {
            const data = resp.data
            setUsageType(userTargetType)
            if (userTargetType !== 'student') {
                const _teachersList = data?.my_teachers || []
                setTeachersList(_teachersList)
                setShowStudents(false)
                setNumberOfTeachersWhoAnswered(dedupeAnswers(_teachersList).length)
                const teacherAnswers = _teachersList.map((teacher) => teacher?.feedback?.emotion)
                filterTeacherData(selectedLabels, buildChartData(emotions_list, teacherAnswers), _teachersList)
            } else {
                setFilterYears(data?.filter_years || [])
                setClassesOfYear(data?.filter_data || {})
                if (selectedYearList === null) {
                    if (!selectedYearFilter.includes('all')) {
                        setSelectedYearList(selectedYearFilter.map((y) => data?.filter_data?.[y] || []).flat())
                    } else {
                        setSelectedYearList(data?.filter_data?.all)
                    }
                }
                const _my_students = (data?.my_class || []).filter((_class) => _class?.data?.length)
                const studentAnswers = dedupeAnswers(_my_students.flatMap((_class) => _class.data || [])).map((item) => item.emotion)
                setNumberOfStudentsWhoAnswered(studentAnswers.length)
                filterData(selectedLabels, buildChartData(listOfEmotions, studentAnswers), _my_students)
                setMyStudents(_my_students)
                setShowStudents(true)
            }
            setDataVersion((version) => version + 1)
        } else {
            notify(t('pleaseTryAgainInAFewMinutestOrContactUpstriveSupport'), 'error')
        }
        setLoadingStaffFilter(false)
        setLoading(false)
        setInitLoading(false)
    }
    // The loader reads a lot of state, so the handlers below reach it through a
    // ref instead of listing all of that state as dependencies.
    const loadMyClassRef = useRef(loadMyClass)
    loadMyClassRef.current = loadMyClass

    useEffect(() => {
        loadMyClassRef.current(startDate, buildLoadQuery(), currentTargetType())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayMode])

    const isNextButtonDisabled = isSameDay(startDate, Date.now())

    const changeDate = useCallback(
        (date) => {
            // MUI emits an Invalid Date while the user is still typing.
            if (!date || !isValid(date)) return
            setLoading(true)
            setStartDate(date)
            loadMyClassRef.current(date, buildLoadQuery(), currentTargetType())
        },
        [setStartDate, buildLoadQuery, currentTargetType]
    )
    const showPreviousDay = useCallback(() => {
        if (!startDate) return
        setLoading(true)
        const previousDay = new Date(startDate.getTime() - MILLISECONDS_PER_DAY)
        setStartDate(previousDay)
        loadMyClassRef.current(previousDay, buildLoadQuery(), currentTargetType())
    }, [startDate, setStartDate, buildLoadQuery, currentTargetType])
    const showNextDay = useCallback(() => {
        if (!startDate || isNextButtonDisabled || startDate.getTime() >= maxDate.getTime()) return
        setLoading(true)
        const nextDay = new Date(startDate.getTime() + MILLISECONDS_PER_DAY)
        setStartDate(nextDay)
        loadMyClassRef.current(nextDay, buildLoadQuery(), currentTargetType())
    }, [startDate, isNextButtonDisabled, maxDate, setStartDate, buildLoadQuery, currentTargetType])

    const handleSelectedLabel = useCallback(
        (label) => {
            if (selectedEmotion !== 'Emotions') setSelectedEmotion('Emotions')
            if (showStudents) setClassUiState({})
            let selectedEmotions = selectedLabels.length === listOfEmotions.length ? [] : [...selectedLabels]
            if (selectedEmotions.includes(label)) {
                selectedEmotions = selectedEmotions.filter((l) => l !== label)
                if (selectedEmotions.length === 0) selectedEmotions = [...listOfEmotions]
            } else {
                selectedEmotions.push(label)
            }
            setSelectedLabels(selectedEmotions)
            setShowContentLabel(false)
            if (showStudents) filterData(selectedEmotions, resultDataChart, myStudents, 'Emotions')
            else filterTeacherData(selectedEmotions)
        },
        [selectedEmotion, showStudents, selectedLabels, listOfEmotions, setSelectedLabels, filterData, filterTeacherData, resultDataChart, myStudents]
    )

    const handleSelectedLabelDropdown = useCallback(
        (labels) => {
            setSelectedLabels(labels)
            setResultDataChart(recolorChart(resultDataChart, labels))
            if (showStudents) {
                setMyVisibleStudents(filterClasses(myStudents, (student) => labels.includes(student.feedback?.emotion)))
            } else {
                setMyVisibleTeachersList((teachersList || []).filter((teacher) => labels.includes(teacher?.feedback?.emotion)))
            }
        },
        [setSelectedLabels, resultDataChart, showStudents, myStudents, teachersList]
    )

    const handleSelectedEmotions = useCallback(
        (label) => {
            if (label === 'All emotions') {
                if (showStudents) setMyVisibleStudents(myStudents)
                else setMyVisibleTeachersList(teachersList)
                setResultDataChart(resultDataChart.map((entry) => ({ ...entry, color: emotions_color[entry.label.toLowerCase()], selected: true })))
                setSelectedLabels(listOfEmotions)
            } else if (label === 'Uncomfortable') {
                handleSelectedLabelDropdown(uncomfortableOf(listOfEmotions, isRegularDisplayMode))
            } else {
                handleSelectedLabelDropdown([label])
            }
            setSelectedEmotion(label)
            setShowContentLabel(false)
        },
        [showStudents, myStudents, teachersList, resultDataChart, setSelectedLabels, listOfEmotions, isRegularDisplayMode, handleSelectedLabelDropdown]
    )

    const openStudent = useCallback((student) => {
        setSelectedStudent(student)
        setOpenStudentFeedback(true)
    }, [])
    const closeStudentFeedback = useCallback(() => {
        setOpenStudentFeedback(false)
    }, [])

    const searchStudents = useCallback(
        (e) => {
            const text = e.target.value
            setSearchText(text)
            if (showStudents) {
                const matching = myStudents.map((_class) => ({ ..._class, data: (_class.data || []).filter((student) => matchesName(student, text)) }))
                filterData(selectedLabels, resultDataChart, matching)
            } else {
                filterTeacherData(
                    selectedLabels,
                    resultDataChart,
                    teachersList.filter((teacher) => matchesName(teacher, text))
                )
            }
        },
        [setSearchText, showStudents, myStudents, teachersList, selectedLabels, resultDataChart, filterData, filterTeacherData]
    )

    const selectAllYears = useCallback(() => {
        setSelectedYearList(classesOfYear['all'])
        setSelectedClassFilter(['all'])
        setSelectedYearFilter(['all'])
    }, [classesOfYear])

    const selectYearFilter = useCallback(
        (year) => {
            if (year === 'all') {
                selectAllYears()
                return
            }
            const withoutAll = selectedYearFilter.filter((y) => y !== 'all')
            const newSelectedYears = withoutAll.includes(year) ? withoutAll.filter((y) => y !== year) : [...withoutAll, year]

            if (newSelectedYears.length === 0) {
                selectAllYears()
            } else {
                setSelectedYearFilter(newSelectedYears)
                setSelectedClassFilter(['all'])
                setSelectedYearList(newSelectedYears.map((y) => classesOfYear[y] || []).flat())
            }
        },
        [selectedYearFilter, classesOfYear, selectAllYears]
    )

    const selectAllClasses = useCallback(() => {
        setSelectedClassFilter(['all'])
    }, [])

    const applyFilter = useCallback(async () => {
        setAppliedYearFilter(selectedYearFilter)
        setAppliedClassFilter(selectedClassFilter)
        const query = {}
        if (!selectedYearFilter.includes('all')) query.years = selectedYearFilter
        if (!selectedClassFilter.includes('all')) query.classes = selectedClassFilter
        setLoadingFilter(true)
        setLoading(true)
        await loadMyClassRef.current(startDate, query)
        setLoadingFilter(false)
    }, [selectedYearFilter, selectedClassFilter, setAppliedYearFilter, setAppliedClassFilter, startDate])

    const applyStaffFilter = useCallback(async () => {
        setLoadingStaffFilter(true)
        setLoading(true)
        await loadMyClassRef.current(startDate, { class: 'staff' }, 'teacher')
        setLoadingStaffFilter(false)
    }, [startDate])

    const openRegularMode = () => {
        setSelectedLabels(emotions_list)
        setLoading(true)
        setSelectedYearList(null)
        setClassUiState({})
        setDisplayMode('regular')
    }
    const openPrimaryMode = () => {
        setSelectedLabels(primary_emotions_list)
        setLoading(true)
        setSelectedYearList(null)
        setClassUiState({})
        setDisplayMode('primary')
    }

    return (
        <div className="classroom-home-container" ref={scrollRef}>
            <div className="max-w-[1522px] m-auto">
                {!!user?.hasPrimaryClasses && !user.hasOnlyPrimaryStudents && !primaryUI && (
                    <div className="flex flex-row gap-[20px] mb-[38px] items-center">
                        <p className="text-[#9392AD] font-semibold text-[18px]">{t('Display')}: </p>
                        <div className="flex flex-row bg-[#FFFFFF] rounded-full">
                            <button
                                type="button"
                                className={`h-[50px] px-[25px] rounded-full justify-center items-center font-bold transition-colors duration-200 ${
                                    isRegularDisplayMode ? 'bg-[#5E639B] text-[#FDDF6D]' : 'text-[#9392AD]'
                                }`}
                                onClick={openRegularMode}
                                disabled={isRegularDisplayMode}
                            >
                                <Collapsedlogo height={30} className={isRegularDisplayMode ? '' : 'collapsed-education-app-icon-not-selected'} />
                            </button>
                            <button
                                type="button"
                                className={`h-[50px] px-[25px] rounded-full justify-center items-center font-bold transition-colors duration-200 ${
                                    !isRegularDisplayMode ? 'bg-[#5E639B] text-[#FDDF6D]' : 'text-[#9392AD]'
                                }`}
                                onClick={openPrimaryMode}
                                disabled={!isRegularDisplayMode}
                            >
                                <CollapsedEarlylogo height={30} className={!isRegularDisplayMode ? '' : 'collapsed-early-app-icon-not-selected'} />
                            </button>
                        </div>
                    </div>
                )}
                {isRegularDisplayMode ? (
                    <Suspense fallback={<ClassroomTrendSkeleton />}>
                        <ClassroomTrend />
                    </Suspense>
                ) : (
                    <Suspense fallback={<AnalyticsHeaderSkeleton primary />}>
                        <PrimaryClassesHeader
                            changeDate={changeDate}
                            isDatePickerOpen={isDatePickerOpen}
                            isNextButtonDisabled={isNextButtonDisabled}
                            setIsDatePickerOpen={setIsDatePickerOpen}
                            showNextDay={showNextDay}
                            showPreviousDay={showPreviousDay}
                            startDate={startDate}
                        />
                    </Suspense>
                )}
                <div className="classroom-chart-container">
                    <Suspense fallback={<SkeletonClassroomChartHeader />}>
                        <ClassroomChartHeader
                            isRegularDisplayMode={isRegularDisplayMode}
                            showPreviousDay={showPreviousDay}
                            showNextDay={showNextDay}
                            isNextButtonDisabled={isNextButtonDisabled}
                            isDatePickerOpen={isDatePickerOpen}
                            setIsDatePickerOpen={setIsDatePickerOpen}
                            startDate={startDate}
                            changeDate={changeDate}
                            maxDate={maxDate}
                            primaryUI={primaryUI}
                            showStudents={showStudents}
                            searchText={searchText}
                            searchStudents={searchStudents}
                            searchRef={searchRef}
                            selectedEmotion={selectedEmotion}
                            showDropdown={showDropdown}
                            setShowDropdown={setShowDropdown}
                            handleSelectedEmotions={handleSelectedEmotions}
                            resultDataChart={resultDataChart}
                            selectedYearFilter={selectedYearFilter}
                            selectYearFilter={selectYearFilter}
                            filterYears={filterYears}
                            selectedClassFilter={selectedClassFilter}
                            setSelectedClassFilter={setSelectedClassFilter}
                            selectedYearList={selectedYearList}
                            selectAllClasses={selectAllClasses}
                            applyFilter={applyFilter}
                            loadingFilter={loadingFilter}
                            applyStaffFilter={applyStaffFilter}
                            loadingStaffFilter={loadingStaffFilter}
                            hideStaffButton={hideStaffButton}
                            numberOfStudentsWhoAnswered={numberOfStudentsWhoAnswered}
                            numberOfTeachersWhoAnswered={numberOfTeachersWhoAnswered}
                            handleSelectedLabel={handleSelectedLabel}
                            loading={loading}
                        />
                    </Suspense>

                    {showStudents && (
                        <div className="classroom-wellbeing-title justify-start classroom-section-enter" ref={titleRef}>
                            <p className="mr-[10px]">
                                {t('yourIndividualStudentsResponses')}
                                {showCommentLabel && <span className="text-[16px] text-[#9392AD] font-semibold"> {t('uncomfortableEmontionsWithComments')}</span>}
                            </p>
                            {loading && <Loading />}
                        </div>
                    )}
                    {/* Stale content dims while the next day/filter loads instead of vanishing. */}
                    <div className="classroom-dim" data-loading={loading && !initLoading}>
                        {!initLoading && showStudents && (
                            <Suspense fallback={<ClassroomSkeletonList />}>
                                <ClassroomStudentsList
                                    scrollRef={scrollRef}
                                    myVisibleStudents={myVisibleStudents}
                                    classUiState={classUiState}
                                    toggleClassUi={toggleClassUi}
                                    openStudent={openStudent}
                                    reduce={reduce}
                                    primaryUI={primaryUI}
                                    t={t}
                                    dataVersion={dataVersion}
                                />
                            </Suspense>
                        )}

                        {!loadingStaffFilter && !showStudents && (
                            <ClassroomTeachersList myVisibleTeachersList={myVisibleTeachersList} openStudent={openStudent} t={t} dataVersion={dataVersion} />
                        )}
                    </div>
                    <CommentTooltip />
                </div>
            </div>
            <FeedBackModal
                modalIsOpen={openStudentFeedback}
                closeModal={closeStudentFeedback}
                student={selectedStudent}
                userType={usageType}
                day={startDate === null ? new Date().getTime() : startDate.getTime()}
                analyticsSource="classroom"
            />
        </div>
    )
}
export default ClassroomHome
